import {
    createDataActionFactory,
    Feature,
    createActionFactory,
    createThunk,
    ThunkDispatch,
    getMatchingFavoriteUnifiedGroup,
    PersonType,
} from '../../../../shared';
import {
    getPackageState,
    lpcLeaveGroupCompletedAction,
    LeaveGroupActionPayload,
} from '../../../../appstate';
import { HubState, packageStateKey } from '../../../state';
import setUnifiedGroupMembershipState from '../../../dataLayer/ows/groups/setMembershipState';
import {
    leaveGroupSuccessNotification,
    leaveGroupErrorNotification,
} from '../../user/groups/notificationBarActions';
import getGroupDetails from '../../../dataLayer/ows/groups/getGroupDetails';
import { getNextItemInList } from '../../../selectors/personaListSelector';
import { getNextUnifiedGroupIdInList } from '../../../selectors/groups/currentUnifiedGroupSelector';
import { removeFavoritePersona } from '../favoriteAddRemoveDataActions';

export const leaveGroupClickedAction = createActionFactory<{
    groupId: string;
    isLastOwner: boolean;
    groupKind: number;
}>('LeaveGroupClickedAction', Feature.UnifiedGroups, action => ({
    isLastOwner: action.payload.isLastOwner,
}));

export const leaveGroupCompletedAction = createDataActionFactory<
    LeaveGroupActionPayload,
    LeaveGroupActionPayload
>('LeaveGroupCompletedAction', Feature.UnifiedGroups, action => ({
    groupId: action.payload.groupId,
}));

export const leaveGroupThunk = (
    groupId: string,
    groupSmtp: string,
    groupName: string,
    isExpiring: boolean,
    isOwner: boolean,
    membershipRequestsCount: number,
    userEmailAddress: string,
    hubPersonaId: string,
    isRetry?: boolean
) =>
    createThunk('LeaveGroup', (dispatch: ThunkDispatch, getState: () => {}): void => {
        const packageState = getPackageState<HubState>(getState(), packageStateKey);
        if (packageState) {
            const nextListItemId =
                getNextItemInList(
                    packageState.appState,
                    packageState.packageState,
                    hubPersonaId,
                    false /*shouldFetchNextItemInSearch*/
                ) || getNextUnifiedGroupIdInList(packageState.appState, packageState.packageState);
            const matchingFavorite = getMatchingFavoriteUnifiedGroup(
                hubPersonaId,
                packageState.appState.data.hubState.favoritePersonsState.favoritePersons
            );

            setUnifiedGroupMembershipState(groupId, groupSmtp, userEmailAddress, 'leave')
                .then(response => {
                    dispatch(
                        leaveGroupCompletedAction.success({
                            groupId,
                            groupName,
                            isExpiring,
                            membershipRequestsCount,
                            userEmailAddress,
                            isOwner,
                            nextListItemId,
                            groupSmtp,
                            hubPersonaId,
                        })
                    );
                    if (matchingFavorite) {
                        dispatch(
                            removeFavoritePersona(
                                hubPersonaId,
                                matchingFavorite.favoriteId,
                                matchingFavorite.info.displayName,
                                PersonType.Group
                            )
                        );
                    }

                    leaveGroupSuccessNotification(groupId, groupName);
                    getGroupDetails(groupSmtp, groupId);
                })
                .catch((error: Error) => {
                    dispatch(
                        leaveGroupCompletedAction.error({
                            groupId,
                            groupName,
                            isExpiring,
                            membershipRequestsCount,
                            userEmailAddress,
                            isOwner,
                            nextListItemId,
                            groupSmtp,
                            hubPersonaId,
                        })
                    );
                    leaveGroupErrorNotification(dispatch)(
                        groupId,
                        groupSmtp,
                        groupName,
                        isExpiring,
                        membershipRequestsCount,
                        userEmailAddress,
                        isOwner,
                        isRetry,
                        hubPersonaId
                    );
                });
        }
    });

export const lpcLeaveGroupCompleted = (hubPersonaId: string) =>
    createThunk('LpcLeaveGroupCompleted', (dispatch: ThunkDispatch, getState: () => {}): void => {
        const packageState = getPackageState<HubState>(getState(), packageStateKey);
        if (packageState) {
            const groupsMap = packageState.appState.data.groupsHubState.unifiedGroupsMap;
            const nextListItemId =
                getNextItemInList(
                    packageState.appState,
                    packageState.packageState,
                    hubPersonaId,
                    false /*shouldFetchNextItemInSearch*/
                ) || getNextUnifiedGroupIdInList(packageState.appState, packageState.packageState);
            const matchingFavorite = getMatchingFavoriteUnifiedGroup(
                hubPersonaId,
                packageState.appState.data.hubState.favoritePersonsState.favoritePersons
            );

            if (groupsMap?.[hubPersonaId]) {
                const group = groupsMap[hubPersonaId];
                dispatch(
                    lpcLeaveGroupCompletedAction({
                        groupId: group.externalDirectoryObjectId,
                        groupName: group.displayName,
                        isExpiring: group.isExpiring,
                        membershipRequestsCount: group.pendingMembershipRequests,
                        userEmailAddress:
                            packageState.appState.data.userConfiguration.settings.UserEmailAddress,
                        isOwner: group.isOwner,
                        nextListItemId,
                        groupSmtp: group.emailAddress,
                        hubPersonaId: group.hubPersonaId,
                    })
                );
                if (matchingFavorite) {
                    dispatch(
                        removeFavoritePersona(
                            hubPersonaId,
                            matchingFavorite.favoriteId,
                            matchingFavorite.info.displayName,
                            PersonType.Group
                        )
                    );
                }
            }
        }
    });
